import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import { changeSeconds } from '../../../app/utils';
import AutocompletePanel from '../../shared/AutocompletePanel';
import Dropdown from '../../shared/Dropdown';
import Switch from '../../shared/Switch';
import TextEditor from '../../shared/TextEditor';
import TextFieldSearch from '../../shared/TextFieldSearch';
import RowTitle from '../RowTitle';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 36,
    },
    inputBlock: {
      borderRadius: 24,
      padding: '12px 16px',
      backgroundColor: color.$19,
      display: 'inline-block',
    },
    tip: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$11,
      marginBottom: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    dropdown: {
      width: 80,
    },
    interval: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 12,
      fontSize: '15px',
      lineHeight: 1,
      color: color.$13,
      '& > span': {
        margin: '0 8px',
      },
    },
    title: {
      fontSize: '15px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: 'normal',
      color: color.$3,
      marginBottom: 6,
    },
    button1Block: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 12,
    },
    inboxDropdown: {
      width: 140,
    },
    customer: {
      position: 'relative',
      width: 223,
      height: '44px',
      marginLeft: 6,
    },
    menuContainer: {
      maxHeight: '192px',
      overflowY: 'auto',
      padding: '8px 0',
      borderRadius: '0px 0px 12px 12px',
    },
    customerRow: {
      position: 'relative',
      padding: '6px 24px',
      margin: 0,
    },
    paperClass: {
      width: 300,
    },
  },
  {
    name: 'AutoExit',
  },
);

// 0 ~ maxNum
const generateDateItem = (maxNum, span = 1) => {
  return Array.from(Array(maxNum / span + 1)).map((v, index) => ({
    text: (index * span).toString(),
    value: index * span,
  }));
};

const dayItem = generateDateItem(1);

const hourItem = generateDateItem(24);

const minuteItem = generateDateItem(60);

const AutoExit = ({
  isChecked,
  onSwitchChange,
  message = '',
  interval = 0,
  inbox,
  userId,
  displayName,
  onChange,
  isSaved,
  disabled,
  onMemberChange,
  onCheckInsightArkProduct,
  members = [],
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [day, setDay] = useState(0);
  const [hour, setHour] = useState(0);
  const [minute, setMinute] = useState(0);
  const [searchCustomerTerm, setSearchCustomerTerm] = useState('');
  const [isCustomerOpen, setIsCustomerOpen] = useState(false);
  const { pathKey } = useSelector(store => store.aiBot);

  const customerServiceItems = [
    {
      text: t(`${pathKey}:unassigned`),
      value: 'unassigned',
    },
    {
      text: t(`${pathKey}:assigned`),
      value: 'private',
    },
    {
      text: t(`${pathKey}:done`),
      value: 'done',
    },
  ];

  const onDayChange = value => {
    onChange({
      interval: (minute + hour * 60 + parseInt(value || 0, 10) * 24 * 60) * 60,
    });
  };

  const onHourChange = value => {
    onChange({
      interval: (minute + parseInt(value || 0, 10) * 60 + day * 24 * 60) * 60,
    });
  };

  const onMinuteChange = value => {
    onChange({
      interval: (parseInt(value || 0, 10) + hour * 60 + day * 24 * 60) * 60,
    });
  };

  const handleChangeSearchCustomerTerm = v => {
    if (!v) {
      onChange({
        displayName: '',
        userId: '',
      });
    }
    setSearchCustomerTerm(v);
    onMemberChange(v);
  };

  const handleCustomerClick = customer => {
    setSearchCustomerTerm('');
    setIsCustomerOpen(false);
    onChange({
      displayName: customer?.displayName,
      userId: customer?.objectId,
    });
  };

  useEffect(() => {
    setDay(changeSeconds(interval, 'day'));
    setHour(changeSeconds(interval, 'hour'));
    setMinute(changeSeconds(interval, 'minute'));
  }, [interval]);

  useEffect(() => {
    if (interval > 86400) {
      onChange({
        interval: 86400,
      });
    }
  }, [interval]);

  return (
    <div className={classes.root}>
      <RowTitle
        title={t(`${pathKey}:triggerSettingContainer.autoExit`)}
        tooltip={t(`${pathKey}:triggerSettingContainer.autoExitTooltip`)}
      />
      <div className={classes.inputBlock}>
        <div className={classes.tip}>
          {t(`${pathKey}:triggerSettingContainer.autoExitSubtitle`)}
          <Switch
            style={{ marginLeft: 4 }}
            onChange={onSwitchChange}
            checked={isChecked}
            disabled={disabled}
          />
        </div>
        <div className={classes.title}>{t(`${pathKey}:intervalTitle`)}</div>
        <div className={classes.interval}>
          <Dropdown
            className={classes.dropdown}
            style={
              isSaved && isChecked && !interval
                ? { border: `solid 1px ${color.$17}` }
                : {}
            }
            menuItems={dayItem}
            value={day}
            onChange={onDayChange}
            disabled={disabled}
          />
          <span>{t(`${pathKey}:day`)}</span>
          <Dropdown
            className={classes.dropdown}
            style={
              isSaved && isChecked && !interval
                ? { border: `solid 1px ${color.$17}` }
                : {}
            }
            menuItems={hourItem}
            value={hour}
            onChange={onHourChange}
            disabled={disabled}
          />
          <span>{t(`${pathKey}:hour`)}</span>
          <Dropdown
            className={classes.dropdown}
            style={
              isSaved && isChecked && !interval
                ? { border: `solid 1px ${color.$17}` }
                : {}
            }
            menuItems={minuteItem}
            value={minute}
            onChange={onMinuteChange}
            disabled={disabled}
          />
          <span>{t(`${pathKey}:minute`)}</span>
        </div>
        <div className={classes.title}>
          {t(`${pathKey}:autoExitInboxTitle`)}
        </div>
        <div className={classes.button1Block}>
          <Dropdown
            isDialog
            className={classes.inboxDropdown}
            menuItems={customerServiceItems}
            value={inbox}
            onChange={v => {
              onCheckInsightArkProduct(() => {
                onChange({
                  inbox: v,
                  displayName: '',
                  userId: '',
                });
              });
            }}
            disabled={disabled}
          />
          {inbox === 'private' && (
            <ClickAwayListener
              onClickAway={() => {
                setIsCustomerOpen(false);
                setSearchCustomerTerm('');
              }}
            >
              <div className={classes.customer}>
                <TextFieldSearch
                  value={searchCustomerTerm || displayName || ''}
                  placeholder={t(
                    `${pathKey}:triggerSettingContainer.endingCardButtonCustomerServiceDropdown`,
                  )}
                  onChange={handleChangeSearchCustomerTerm}
                  onFocus={() => setIsCustomerOpen(true)}
                  disabled={disabled}
                  showClearButton={false}
                  status={isSaved && !userId ? 'error' : 'default'}
                />
                {!disabled && isCustomerOpen && members.length > 0 && (
                  <AutocompletePanel
                    paperClass={classes.paperClass}
                    menuContainerClass={classes.menuContainer}
                    body1Class={classes.customerRow}
                    dataArray={members}
                    value="displayName"
                    extraContent=""
                    extraPicture
                    handleClick={handleCustomerClick}
                    popup
                  />
                )}
              </div>
            </ClickAwayListener>
          )}
        </div>
        <TextEditor
          onChange={v => onChange({ message: v })}
          origriData={message}
          placeholder={t(
            `${pathKey}:triggerSettingContainer.autoExitPlaceholder`,
          )}
          style={{ width: 601 }}
          isError={isSaved && isChecked && !message}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default AutoExit;
